import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../App.css";
import { getSubscriptionTypesPublic } from "../services/authService";
import playstore from "../images/playstore.png";
import appstore from "../images/appstore.svg";
import screenCoachSeance from "../images/screen-coach-seance.jpg";
import screenCoachDashboard from "../images/screen-coach-dashboard.jpg";
import screenCoachOverview from "../images/screen-coach-overview.jpg";
import screenCoachProgrammer from "../images/screen-coach-programmer.jpg";
import screenSportifSeances from "../images/screen-sportif-seances.jpg";
import screenSportifAmis from "../images/screen-sportif-amis.jpg";
import screenSportifPoids from "../images/screen-sportif-poids.jpg";
import screenWebOverview from "../images/screen-web-overview.png";
import screenWebEditor from "../images/screen-web-editor.png";
import screenWebStats from "../images/screen-web-stats.png";
import appIcon from "../images/favicon.png";

function FadeInSection({ children, className = "" }) {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					observer.unobserve(entry.target);
				}
			},
			{ threshold: 0.15 }
		);
		if (ref.current) observer.observe(ref.current);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			ref={ref}
			className={`fade-in-section ${visible ? "visible" : ""} ${className}`}
		>
			{children}
		</div>
	);
}

const FALLBACK_PLANS = {
	starter: ["Entraînements illimités", "Synchronisation cloud", "Templates personnalisés", "Dashboard mobile", "Jusqu’à 10 élèves", "Planification avancée"],
	pro: ["Entraînements illimités", "Synchronisation cloud", "Templates personnalisés", "Dashboard mobile", "Planification avancée", "Dashboard Coach web", "Jusqu’à 100 élèves"],
};

const StoreBadges = ({ size = "default" }) => (
	<div className={`hv2-stores ${size === "large" ? "hv2-stores-large" : ""}`}>
		<a
			href="https://play.google.com/store/apps/details?id=com.frenchmatrix.GymNote"
			target="_blank"
			rel="noopener noreferrer"
			className="hv2-store-link"
		>
			<img src={playstore} alt="Get it on Play Store" className="hv2-store-img" />
		</a>
		<a
			href="https://apps.apple.com/fr/app/gymnote-suivi-muscu-coach/id6470710692"
			target="_blank"
			rel="noopener noreferrer"
			className="hv2-store-link"
		>
			<img src={appstore} alt="Download on the App Store" className="hv2-store-img" />
		</a>
	</div>
);

const FAQ_ITEMS = [
	{
		q: "L'app fonctionne-t-elle sans connexion ?",
		a: "Oui, GymNote est 100% offline-first. Toutes tes données sont stockées localement sur ton téléphone. La synchronisation cloud se fait automatiquement dès que tu retrouves une connexion.",
	},
	{
		q: "Puis-je suivre plusieurs élèves ?",
		a: "Oui, avec un abonnement Coach (Starter ou Pro). Le plan Starter te permet de suivre jusqu'à 10 élèves, le plan Pro jusqu'à 100. Chaque élève accepte ton invitation depuis son app.",
	},
	{
		q: "Comment fonctionne la synchronisation ?",
		a: "La synchronisation est automatique — elle se déclenche toutes les 10 secondes quand tu es connecté. En cas de conflit, GymNote conserve toujours la version la plus récente.",
	},
	{
		q: "Est-ce que mes données sont sauvegardées ?",
		a: "Absolument. Tes entraînements, mesures et exercices sont sauvegardés en temps réel sur nos serveurs sécurisés. Tu peux aussi exporter tes données à tout moment depuis les paramètres.",
	},
	{
		q: "Puis-je annuler mon abonnement à tout moment ?",
		a: "Oui, sans engagement. Tu peux annuler ton abonnement depuis ton dashboard directement dans l'app. La résiliation prend effet à la fin de la période en cours.",
	},
	{
		q: "L'app est-elle disponible sur iOS et Android ?",
		a: "GymNote est disponible sur les deux plateformes : télécharge-la sur l'App Store (iPhone/iPad) ou sur Google Play (Android). L'expérience est identique sur les deux.",
	},
	{
		q: "Comment inviter un élève ?",
		a: "Depuis l'app, active le mode Coach puis rends-toi dans la section GymPartners. Génère un code d'invitation unique que tu envoies à ton élève. Une fois accepté, tu as accès à ses séances.",
	},
	{
		q: "Y a-t-il une période d'essai ?",
		a: "Oui, tu bénéficies de 7 jours gratuits pour tester le mode Coach sans aucune restriction. Aucune carte bancaire requise pour démarrer l'essai.",
	},
];

const TESTIMONIALS = [
	{
		quote: "Depuis que j'utilise GymNote, je programme les séances de mes élèves en 10 minutes au lieu d'une heure. Le dashboard web est vraiment pratique pour ajuster en temps réel.",
		name: "Thomas R.",
		role: "Coach sportif indépendant",
		initials: "TR",
		avatarClass: "testimonial-avatar-orange",
	},
	{
		quote: "L'app est super fluide et le mode hors-ligne est parfait pour la salle. Je vois mes perfs précédentes directement pendant ma séance, ça motive vraiment à se dépasser.",
		name: "Marie L.",
		role: "Athlète amateur",
		initials: "ML",
		avatarClass: "testimonial-avatar-teal",
	},
	{
		quote: "J'ai 23 élèves et GymNote Pro me permet de tout gérer depuis mon téléphone ou mon ordinateur. La synchronisation instantanée change vraiment la façon dont je travaille.",
		name: "Julien M.",
		role: "Coach CrossFit certifié",
		initials: "JM",
		avatarClass: "testimonial-avatar-purple",
	},
];

function FaqAccordion() {
	const [openIndex, setOpenIndex] = useState(null);

	const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

	return (
		<FadeInSection>
			<section className="hv2-sportif faq-section" style={{ maxWidth: "820px" }}>
				<div className="hv2-section-head faq-section-head">
					<div className="hv2-eyebrow hv2-eyebrow-neutral">
						<span className="hv2-eyebrow-num">05</span>
						<span>FAQ</span>
					</div>
					<h2 className="hv2-h2">
						<span className="hv2-thin">TES</span>
						<span className="hv2-bold hv2-teal">QUESTIONS</span>
					</h2>
					<p className="hv2-sub-center">Tout ce que tu veux savoir avant de te lancer.</p>
				</div>
				<div className="faq-list">
					{FAQ_ITEMS.map((item, i) => (
						<div
							key={i}
							className={`faq-item${openIndex === i ? " open" : ""}`}
						>
							<button
								className="faq-question"
								onClick={() => toggle(i)}
								aria-expanded={openIndex === i}
							>
								<span>{item.q}</span>
								<span className="faq-question-icon" aria-hidden="true">+</span>
							</button>
							<div className="faq-answer">
								<p>{item.a}</p>
							</div>
						</div>
					))}
				</div>
			</section>
		</FadeInSection>
	);
}

function TestimonialsSection() {
	return (
		<FadeInSection>
			<section className="hv2-sportif testimonials-section">
				<div className="hv2-section-head">
					<div className="hv2-eyebrow hv2-eyebrow-neutral">
						<span className="hv2-eyebrow-num">ILS</span>
						<span>Témoignages</span>
					</div>
					<h2 className="hv2-h2">
						<span className="hv2-thin">ILS</span>
						<span className="hv2-bold hv2-teal">EN PARLENT</span>
					</h2>
				</div>
				<div className="testimonials-grid">
					{TESTIMONIALS.map((t, i) => (
						<article key={i} className="testimonial-card">
							<p className="testimonial-quote">{t.quote}</p>
							<div className="testimonial-author">
								<div className={`testimonial-avatar ${t.avatarClass}`}>{t.initials}</div>
								<div>
									<p className="testimonial-name">{t.name}</p>
									<p className="testimonial-role">{t.role}</p>
								</div>
							</div>
						</article>
					))}
				</div>
			</section>
		</FadeInSection>
	);
}

function Home() {
	const [billingPeriod, setBillingPeriod] = useState("monthly");
	const [plans, setPlans] = useState(null);

	useEffect(() => {
		const fetchPlans = async () => {
			const result = await getSubscriptionTypesPublic();
			if (result.success) {
				setPlans(result.types);
			}
		};
		fetchPlans();
	}, []);

	return (
		<div className="page hv2">
			<div className="page-content hv2-content">

				{/* ===== Section 1 — HERO COACH ===== */}
				<section className="hv2-hero">
					<div className="hv2-hero-bg" aria-hidden="true">
						<div className="hv2-hero-rings"></div>
						<div className="hv2-hero-bokeh hv2-hero-bokeh-1"></div>
						<div className="hv2-hero-bokeh hv2-hero-bokeh-2"></div>
						<div className="hv2-hero-bokeh hv2-hero-bokeh-3"></div>
					</div>

					<div className="hv2-hero-text">
						<div className="hv2-hero-brand hv2-anim-1">
							<svg className="hv2-rocket-mini" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
								<path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
								<path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
								<path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
							</svg>
							<span className="hv2-hero-brand-text">GymNote</span>
						</div>
						<h1 className="hv2-h1 hv2-anim-2">
							<span className="hv2-thin">FAIS-LES</span>
							<span className="hv2-bold hv2-orange">PROGRESSER.</span>
						</h1>
						<p className="hv2-sub hv2-anim-3">
							Programme leurs séances, suis leurs progrès, ajuste en direct. Sur
							ton téléphone, et sur un dashboard web pour les coachs Pro.
						</p>
					</div>

					<div className="hv2-hero-phones hv2-anim-3">
						<div className="hv2-phone hv2-phone-back">
							<span className="hv2-phone-island" aria-hidden="true"></span>
							<img src={screenCoachDashboard} alt="Dashboard mobile coach" />
						</div>
						<div className="hv2-phone hv2-phone-front">
							<span className="hv2-phone-island" aria-hidden="true"></span>
							<img src={screenCoachSeance} alt="Mode coach séance" />
						</div>
						<span className="hv2-floater hv2-floater-1">Régularité 80%</span>
						<span className="hv2-floater hv2-floater-2">Progression 60%</span>
						<span className="hv2-floater hv2-floater-3">Régression 8%</span>
						<span className="hv2-floater hv2-floater-4">4.5 séances/sem</span>
					</div>

					<div className="hv2-hero-cta-column hv2-anim-4">
						<div className="hv2-hero-app-icon">
							<img src={appIcon} alt="Icône GymNote" />
						</div>
						<a
							href="https://play.google.com/store/apps/details?id=com.frenchmatrix.GymNote"
							target="_blank"
							rel="noopener noreferrer"
							className="hv2-hero-store-link"
						>
							<img src={playstore} alt="Disponible sur Google Play" />
						</a>
						<a
							href="https://apps.apple.com/fr/app/gymnote-suivi-muscu-coach/id6470710692"
							target="_blank"
							rel="noopener noreferrer"
							className="hv2-hero-store-link"
						>
							<img src={appstore} alt="Télécharger dans l’App Store" />
						</a>
					</div>
				</section>

				{/* ===== Section 2 — DUALITÉ ===== */}
				<FadeInSection>
					<section className="hv2-duality">
						<div className="hv2-section-head">
							<div className="hv2-eyebrow hv2-eyebrow-neutral">
								<span className="hv2-eyebrow-num">01</span>
								<span>Dualité</span>
							</div>
							<h2 className="hv2-h2">
								<span className="hv2-thin">UNE APP</span>
								<span className="hv2-bold">
									<span className="hv2-teal">DEUX</span>{" "}
									<span className="hv2-orange">UNIVERS</span>
								</span>
							</h2>
							<p className="hv2-sub-center">Fais ton choix aujourd’hui.</p>
						</div>

						<div className="hv2-duo-grid">
							<article className="hv2-duo-card hv2-duo-card-teal">
								<div className="hv2-duo-card-bar" aria-hidden="true"></div>
								<div className="hv2-duo-card-icon" aria-hidden="true">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
										<polyline points="3,17 9,11 13,15 21,7" />
										<polyline points="14,7 21,7 21,14" />
									</svg>
								</div>
								<span className="hv2-duo-tag">+ Mode</span>
								<h3 className="hv2-duo-title">SPORTIF</h3>
								<ul className="hv2-duo-list">
									<li>Suivi de tes perfs</li>
									<li>Bats tes records</li>
									<li>Visualise ta progression</li>
									<li>Partage avec tes Gymbros</li>
								</ul>
								<span className="hv2-duo-badge hv2-duo-badge-teal">100% Gratuit</span>
							</article>

							<article className="hv2-duo-card hv2-duo-card-orange">
								<div className="hv2-duo-card-bar" aria-hidden="true"></div>
								<div className="hv2-duo-card-icon" aria-hidden="true">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
										<circle cx="12" cy="12" r="9.5" />
										<circle cx="12" cy="12" r="6" />
										<circle cx="12" cy="12" r="2.5" />
									</svg>
								</div>
								<span className="hv2-duo-tag">+ Mode</span>
								<h3 className="hv2-duo-title">COACH</h3>
								<ul className="hv2-duo-list">
									<li>Suivi de tes élèves</li>
									<li>Planification d’entraînements</li>
									<li>Tableau de bord détaillé</li>
									<li>Accès à la version Web</li>
								</ul>
								<span className="hv2-duo-badge hv2-duo-badge-orange">Starter & Pro</span>
							</article>
						</div>
					</section>
				</FadeInSection>

				{/* ===== Section 3 — SHOWCASE COACH ===== */}
				<FadeInSection>
					<section className="hv2-coach-showcase">
						<div className="hv2-section-head">
							<div className="hv2-eyebrow hv2-eyebrow-coach">
								<span className="hv2-eyebrow-num">02</span>
								<span>Coaching mobile</span>
							</div>
							<h2 className="hv2-h2">
								<span className="hv2-thin">ACTIVE</span>
								<span className="hv2-bold hv2-orange">L’ŒIL DU COACH</span>
							</h2>
							<p className="hv2-sub-center">
								Tes élèves dans la poche. Programme, observe, corrige — sans
								jamais sortir de ton téléphone.
							</p>
						</div>

						<div className="hv2-feature-grid">
							<article className="hv2-feature">
								<span className="hv2-feature-num">01</span>
								<div className="hv2-feature-phone">
									<img src={screenCoachProgrammer} alt="Modale Programmer un entraînement" />
								</div>
								<h3 className="hv2-feature-title">PLANIFIE</h3>
								<p className="hv2-feature-text">
									Choix des exercices, séries, charges, jusqu’à 12 semaines à
									l’avance. Tu programmes une fois, GymNote remplit le calendrier.
								</p>
							</article>

							<article className="hv2-feature">
								<span className="hv2-feature-num">02</span>
								<div className="hv2-feature-phone">
									<img src={screenCoachOverview} alt="Dashboard mobile vue d’ensemble" />
								</div>
								<h3 className="hv2-feature-title">SUIS</h3>
								<p className="hv2-feature-text">
									Indicateurs de régularité, progression, régression. Une vue
									d’ensemble qui te dit qui décroche, qui cartonne.
								</p>
							</article>

							<article className="hv2-feature">
								<span className="hv2-feature-num">03</span>
								<div className="hv2-feature-phone">
									<img src={screenCoachSeance} alt="Édition d'une séance en mode coach" />
								</div>
								<h3 className="hv2-feature-title">AJUSTE</h3>
								<p className="hv2-feature-text">
									Édite chaque séance pendant qu’elle se passe. Tes élèves voient
									les mises à jour en temps réel.
								</p>
							</article>
						</div>
					</section>
				</FadeInSection>

				{/* ===== Section 4 — DASHBOARD WEB SPOTLIGHT ===== */}
				<FadeInSection>
					<section className="hv2-spotlight">
						<div className="hv2-spotlight-grid">
							<div className="hv2-spotlight-visual">
								<div className="hv2-spotlight-stack">
									<div className="hv2-spotlight-browser hv2-browser-back">
										<div className="hv2-browser-bar">
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<div className="hv2-browser-url">gymnote.fr/coach — stats</div>
										</div>
										<img src={screenWebStats} alt="Dashboard Web — statistiques détaillées" />
									</div>
									<div className="hv2-spotlight-browser hv2-browser-mid">
										<div className="hv2-browser-bar">
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<div className="hv2-browser-url">gymnote.fr/coach — éditeur</div>
										</div>
										<img src={screenWebEditor} alt="Dashboard Web — éditeur de séances" />
									</div>
									<div className="hv2-spotlight-browser hv2-browser-front">
										<div className="hv2-browser-bar">
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<span className="hv2-browser-dot" aria-hidden="true"></span>
											<div className="hv2-browser-url">gymnote.fr/coach</div>
										</div>
										<img src={screenWebOverview} alt="Dashboard Web — vue d’ensemble des élèves" />
									</div>
								</div>
							</div>
							<div className="hv2-spotlight-text">
								<div className="hv2-eyebrow hv2-eyebrow-coach">
									<span className="hv2-eyebrow-num">03</span>
									<span>Dashboard Web — Pro</span>
								</div>
								<h2 className="hv2-h2 hv2-h2-left">
									<span className="hv2-thin">RIEN</span>
									<span className="hv2-bold hv2-orange">NE T’ÉCHAPPE</span>
								</h2>
								<p className="hv2-sub">
									Le Dashboard Web Pro étend le mode coach sur ton ordinateur.
									Calendrier multi-élèves, édition de séances rapide, statistiques
									détaillées.
								</p>
								<ul className="hv2-bullet-list">
									<li>
										<span className="hv2-bullet-dot"></span>
										<span>Calendrier mensuel pour chaque élève</span>
									</li>
									<li>
										<span className="hv2-bullet-dot"></span>
										<span>Édition rapide de séances et templates</span>
									</li>
									<li>
										<span className="hv2-bullet-dot"></span>
										<span>Stats de progression et de régression</span>
									</li>
								</ul>
								<Link to="/coach" className="hv2-link-cta">
									Découvrir la version Web <span>→</span>
								</Link>
							</div>
						</div>
					</section>
				</FadeInSection>

				{/* ===== Section 5 — MODE SPORTIF (secondary) ===== */}
				<FadeInSection>
					<section className="hv2-sportif">
						<div className="hv2-section-head">
							<div className="hv2-eyebrow hv2-eyebrow-sportif">
								<span className="hv2-eyebrow-num">04</span>
								<span>Mode sportif — gratuit</span>
							</div>
							<h2 className="hv2-h2">
								<span className="hv2-thin">DÉPASSE-TOI</span>
								<span className="hv2-bold hv2-teal">À CHAQUE SÉANCE</span>
							</h2>
							<p className="hv2-sub-center">
								Le mode sportif reste 100% gratuit, à vie. Tout le suivi perso —
								sans abonnement.
							</p>
						</div>

						<div className="hv2-feature-grid hv2-feature-grid-secondary">
							<article className="hv2-feature hv2-feature-teal">
								<div className="hv2-feature-phone hv2-feature-phone-sm">
									<img src={screenSportifSeances} alt="Liste des séances sportif" />
								</div>
								<h3 className="hv2-feature-title hv2-teal">TES PERFS, EN MAIN</h3>
								<p className="hv2-feature-text">
									Tes anciennes perfs s’affichent direct, à toi de les dépasser.
								</p>
							</article>

							<article className="hv2-feature hv2-feature-teal">
								<div className="hv2-feature-phone hv2-feature-phone-sm">
									<img src={screenSportifAmis} alt="Fil d'amis Gymbros" />
								</div>
								<h3 className="hv2-feature-title hv2-teal">SUIS TES GYMBROS</h3>
								<p className="hv2-feature-text">
									Like, commente, motive. Tes potes voient tes records, tu vois
									les leurs.
								</p>
							</article>

							<article className="hv2-feature hv2-feature-teal">
								<div className="hv2-feature-phone hv2-feature-phone-sm">
									<img src={screenSportifPoids} alt="Suivi du poids de corps" />
								</div>
								<h3 className="hv2-feature-title hv2-teal">TON POIDS, TON HISTOIRE</h3>
								<p className="hv2-feature-text">
									Suivi du poids de corps + reprise rapide de ta dernière séance,
									en un tap.
								</p>
							</article>
						</div>
					</section>
				</FadeInSection>

				{/* ===== Section 5 — TÉMOIGNAGES ===== */}
				<TestimonialsSection />

				{/* ===== Section 6 — FAQ ===== */}
				<FaqAccordion />

				{/* ===== Section 7 — LANCE-TOI (rocket CTA + tarifs intégrés) ===== */}
				<FadeInSection>
					<section className="hv2-final hv2-final-with-pricing">
						<div className="hv2-final-rocket-wrap" aria-hidden="true">
							<div className="hv2-rocket-trail">
								<span></span><span></span><span></span><span></span>
							</div>
							<svg className="hv2-rocket-big" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
								<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
								<path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
								<path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
								<path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
							</svg>
						</div>
						<div className="hv2-eyebrow hv2-eyebrow-coach">
							<span className="hv2-eyebrow-num">07</span>
							<span>Lance-toi</span>
						</div>
						<h2 className="hv2-h2 hv2-h2-center">
							<span className="hv2-thin">PRÊT À</span>
							<span className="hv2-bold hv2-orange">DÉMARRER ?</span>
						</h2>
						<p className="hv2-sub-center">
							Gratuit pour les sportifs. Quelques euros par mois pour les coachs.
						</p>

						<div className="hv2-pricing-inline">
							<div className="billing-toggle billing-toggle-home">
								<span className={billingPeriod === "monthly" ? "active" : ""}>
									Mensuel
								</span>
								<label className="toggle-switch">
									<input
										type="checkbox"
										checked={billingPeriod === "yearly"}
										onChange={() =>
											setBillingPeriod(billingPeriod === "monthly" ? "yearly" : "monthly")
										}
									/>
									<span className="toggle-slider"></span>
								</label>
								<span className={billingPeriod === "yearly" ? "active" : ""}>
									Annuel <span className="billing-savings">-17%</span>
								</span>
							</div>

							<div className="pricing-cards">
								{(() => {
									const starterPlans = plans?.filter(p => p.libelle?.toLowerCase().includes("starter") && p.prix > 0) || [];
									const proPlans = plans?.filter(p => p.libelle?.toLowerCase().includes("pro") && p.prix > 0) || [];
									const starterMonthly = starterPlans.find(p => p.dureeMois === 1);
									const starterYearly = starterPlans.find(p => p.dureeMois === 12);
									const proMonthly = proPlans.find(p => p.dureeMois === 1);
									const proYearly = proPlans.find(p => p.dureeMois === 12);

									const starterFeatures = (starterMonthly || starterYearly)?.fonctionnalites?.map(f => f.libelle) || FALLBACK_PLANS.starter;
									const proFeatures = (proMonthly || proYearly)?.fonctionnalites?.map(f => f.libelle) || FALLBACK_PLANS.pro;

									const starterPlan = billingPeriod === "yearly" && starterYearly ? starterYearly : starterMonthly;
									const starterPrice = starterPlan?.prix ?? (billingPeriod === "monthly" ? 9.99 : 99.99);
									const proPlan = billingPeriod === "yearly" && proYearly ? proYearly : proMonthly;
									const proPrice = proPlan?.prix ?? (billingPeriod === "monthly" ? 19.99 : 199.99);
									const starterIsYearly = starterPlan?.dureeMois === 12;
									const proIsYearly = proPlan?.dureeMois === 12;

									return (
										<>
											<div className="pricing-card">
												<p className="pricing-name">Starter</p>
												<p className="pricing-price">
													{starterPrice}€<span>{starterIsYearly ? "/an" : "/mois"}</span>
												</p>
												{starterIsYearly && (
													<p className="pricing-equivalent">soit {(starterPrice / 12).toFixed(2)}€/mois</p>
												)}
												<ul className="pricing-features">
													{starterFeatures.map((f, i) => <li key={i}>{f}</li>)}
												</ul>
											</div>
											<div className="pricing-card pricing-card-featured">
												<p className="pricing-name">Pro</p>
												<p className="pricing-price">
													{proPrice}€<span>{proIsYearly ? "/an" : "/mois"}</span>
												</p>
												{proIsYearly && (
													<p className="pricing-equivalent">soit {(proPrice / 12).toFixed(2)}€/mois</p>
												)}
												<ul className="pricing-features">
													{proFeatures.map((f, i) => <li key={i}>{f}</li>)}
												</ul>
											</div>
										</>
									);
								})()}
							</div>
						</div>

						<p className="hv2-final-action">
							Pour t’abonner, c’est dans l’app que ça se passe&nbsp;!
						</p>
						<StoreBadges size="large" />
						<p className="hv2-final-quote">
							« C’est une fusée ? Pas loin, c’est GymNote. »
						</p>
					</section>
				</FadeInSection>


			</div>
		</div>
	);
}

export default Home;
